/**
 * Swallows the `click` the browser fires at the end of a drag.
 *
 * Two things make that click land somewhere unhelpful:
 *
 * - the dragged object travels with the pointer, so the release often happens on top of a
 *   DIFFERENT object, and the click goes to that one
 * - when `mousedown` and `mouseup` have different targets the browser does not fire the
 *   click on either of them, but on their nearest common ancestor. Drag one object over
 *   another inside the same frame and the click lands on THE FRAME.
 *
 * Either way the selection jumps away from the object the user has just moved.
 *
 * This used to be a flag that every click handler had to remember to check, which the frame,
 * icon and svg containers did not — and no per-handler check can cover the common-ancestor
 * case anyway, since the element that receives the click may be one nobody thought about.
 * A single capture-phase listener on `window` runs before React's own delegated listeners
 * (React 18 attaches those to the root container), so the click never reaches the tree at
 * all and no handler has to know about any of this.
 */

/** Safety net: drop the listener if the expected click never arrives (pointer released
 *  outside the window, drag cancelled by the OS, …). */
const CLICK_SUPPRESS_TIMEOUT_MS = 300

let cancelPendingSuppression: (() => void) | null = null

/**
 * Suppress the single next `click`, wherever it lands. Call on drag end, after a drag that
 * actually moved something.
 */
export const suppressNextClick = (): void => {
    // a previous suppression that never fired must not outlive this one
    cancelPendingSuppression?.()

    const stop = () => {
        window.removeEventListener("click", handleClick, true)
        clearTimeout(timer)
        cancelPendingSuppression = null
    }

    const handleClick = (event: MouseEvent) => {
        event.stopPropagation()
        event.preventDefault()
        stop()
    }

    const timer = setTimeout(stop, CLICK_SUPPRESS_TIMEOUT_MS)
    cancelPendingSuppression = stop

    window.addEventListener("click", handleClick, true)
}
